/**
 * useWebSocket
 *
 * Stable WebSocket hook — the connect callback only re-runs when url or
 * userId actually changes, never on parent re-renders.
 *
 * Stale-close protection: each socket's onclose checks whether it is
 * still the *current* socket before retrying. If the effect cleaned up
 * (wsRef.current = null) or a new socket was already created, the
 * handler is a no-op.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type WsStatus = 'connecting' | 'open' | 'closed' | 'error';

export interface WsMessage {
  type: string;
  [key: string]: unknown;
}

interface UseWebSocketOptions {
  url: string;
  userId: string;
  onMessage: (msg: WsMessage) => void;
  onOpen?: () => void;
  onClose?: () => void;
  maxRetries?: number;
}

const BASE_DELAY_MS = 1_000;
const MAX_DELAY_MS  = 30_000;
const PING_INTERVAL = 45_000;

export function useWebSocket({
  url,
  userId,
  onMessage,
  onOpen,
  onClose,
  maxRetries = 10,
}: UseWebSocketOptions) {
  // Callbacks in refs — changing them never recreates `connect`
  const onMessageRef = useRef(onMessage);
  const onOpenRef    = useRef(onOpen);
  const onCloseRef   = useRef(onClose);
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);
  useEffect(() => { onOpenRef.current    = onOpen;    }, [onOpen]);
  useEffect(() => { onCloseRef.current   = onClose;   }, [onClose]);

  const wsRef    = useRef<WebSocket | null>(null);
  const retryRef = useRef(0);
  const pingRef  = useRef<ReturnType<typeof setInterval> | null>(null);

  const [status, setStatus] = useState<WsStatus>('connecting');

  const stopPing = useCallback(() => {
    if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
  }, []);

  const startPing = useCallback((ws: WebSocket) => {
    stopPing();
    pingRef.current = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, PING_INTERVAL);
  }, [stopPing]);

  // Stable — only recreated when url/userId change
  const connect = useCallback(() => {
    const fullUrl = `${url}?userId=${encodeURIComponent(userId)}`;
    const ws = new WebSocket(fullUrl);
    wsRef.current = ws;
    setStatus('connecting');

    ws.onopen = () => {
      // Guard: a newer socket may have replaced this one already
      if (wsRef.current !== ws) { ws.close(); return; }
      retryRef.current = 0;
      setStatus('open');
      startPing(ws);
      ws.send(JSON.stringify({ type: 'presence-sync' }));
      onOpenRef.current?.();
    };

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data) as WsMessage;
        onMessageRef.current(msg);
      } catch {
        console.warn('useWebSocket: bad message', evt.data);
      }
    };

    ws.onerror = () => {
      if (wsRef.current !== ws) return;
      setStatus('error');
    };

    ws.onclose = () => {
      // If this socket is no longer current it was replaced intentionally — ignore
      if (wsRef.current !== ws) return;

      stopPing();
      setStatus('closed');
      onCloseRef.current?.();

      if (retryRef.current < maxRetries) {
        const delay = Math.min(BASE_DELAY_MS * 2 ** retryRef.current, MAX_DELAY_MS);
        retryRef.current += 1;
        console.log(`WebSocket reconnecting in ${delay}ms (attempt ${retryRef.current})`);
        setTimeout(connect, delay);
      } else {
        console.error('WebSocket: max retries reached');
        setStatus('error');
      }
    };
  }, [url, userId, startPing, stopPing, maxRetries]);

  useEffect(() => {
    connect();
    return () => {
      // Nulling wsRef BEFORE closing means the onclose handler sees a
      // stale socket and will not schedule a reconnect.
      const ws = wsRef.current;
      wsRef.current = null;
      stopPing();
      ws?.close();
    };
  }, [connect, stopPing]);

  const send = useCallback((data: WsMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    } else {
      console.warn('useWebSocket: send called while not open', data.type);
    }
  }, []);

  return { status, send };
}
